import { useLazyQuery, useMutation } from "@apollo/client";
import { Alert, Box, Grid, MenuItem, TextField, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { FacilityPicker } from "components/FacilityPicker";
import AsyncAutocomplete from "components/Forms/AsyncAutocomplete";
import { CheckboxField } from "components/Forms/CheckboxField";
import { DateField } from "components/Forms/DateField";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { IssueListButton } from "components/Forms/IssueListButton";
import {
  WASTE_CONTAINER_TYPES,
  WASTE_CONTAINER_TYPE_BY_CODE,
  isWasteContainerTypeCode,
} from "encamp-shared/src/constants/wasteContainerTypes";
import { safeParseFloat } from "encamp-shared/src/tierii/utilities";
import {
  FacilityWasteStreamOptionsQuery,
  GetWasteContainerQuery,
  Issue,
  IssueLevel,
  UnitType,
  WasteAccumulationAreaType,
  WasteContainerCreateInput,
  WasteContainerUpdateInput,
} from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { Controller, FormProvider } from "react-hook-form";
import { toUtcMidnightIsoFromDateLike } from "util/dates";
import { hasCriticalIssues } from "util/forms";

import {
  CREATE_WASTE_CONTAINER_MUTATION,
  FACILITY_WASTE_STREAM_OPTIONS_QUERY,
  UPDATE_WASTE_CONTAINER_MUTATION,
  VALIDATE_WASTE_CONTAINER_INPUT,
} from "./api";
import { toGraphqlUnitType, UNIT_LABELS } from "./containerDisplay";

type WasteContainerDetail = NonNullable<GetWasteContainerQuery["wasteContainer"]>;

type WasteStreamOption = NonNullable<
  NonNullable<FacilityWasteStreamOptionsQuery["facility"]>["wasteStreams"]
>["items"][number];

type WasteContainerFormValues = {
  facilityId: string;
  containerLabel: string;
  containerType: string;
  capacity: string;
  capacityUnit: UnitType | null;
  isPermanent: boolean;
  isFlowThrough: boolean;
  wasteStreamId: string | null;
  initialAmount: string;
  initialAmountUnit: UnitType | null;
  isEstimate: boolean;
  accumulationStartDate: string | null;
  movedToCentralDate: string | null;
  sqgOver200Miles: boolean;
  estimatedPickupDate: string | null;
};

const UNIT_SELECT_ITEMS = Object.values(UnitType).map((unit) => ({
  value: unit,
  display: UNIT_LABELS[unit],
}));

const CONTAINER_TYPE_SELECT_ITEMS = WASTE_CONTAINER_TYPES.map((type) => ({
  value: type.code,
  display: `${type.label} (${type.code})`,
}));

function toDateTimeInput(isoDate: string | null): string | undefined {
  if (!isoDate) {
    return undefined;
  }
  return toUtcMidnightIsoFromDateLike(DateTime.fromISO(isoDate));
}

/**
 * Fields whose form→input mapping is byte-for-byte the same for create and
 * update. Both input mappers spread this so a new shared column is wired in
 * one place instead of drifting between the two. containerLabel is not here:
 * its blank handling diverges by mode (create auto-generates a label, edit
 * rejects a blank), like the nullable fields in toContainerUpdateInput.
 */
function toSharedContainerFields(values: WasteContainerFormValues) {
  return {
    containerType: values.containerType,
    isPermanent: values.isPermanent,
    isFlowThrough: values.isFlowThrough,
    isEstimate: values.isEstimate,
    sqgOver200Miles: values.sqgOver200Miles,
  };
}

/**
 * The validator takes the create-input shape for both modes; edit maps its
 * identity fields onto it, plus the container's CURRENT area so placement
 * rules (tanks are central-only) block inline instead of surfacing as a raw
 * mutation error after Save.
 */
function toContainerInput(
  values: WasteContainerFormValues,
  mode: "create" | "edit",
  currentAccumulationAreaId?: string | null
): WasteContainerCreateInput {
  return {
    ...toSharedContainerFields(values),
    // Create treats a blank label as "auto-generate"; edit sends the trimmed
    // value verbatim so a cleared field reaches the validator/mutation as ""
    // and is rejected rather than silently reverting to the stored label.
    containerLabel:
      mode === "create" ? values.containerLabel.trim() || undefined : values.containerLabel.trim(),
    facilityId: values.facilityId,
    capacity: safeParseFloat(values.capacity) ?? undefined,
    capacityUnit: values.capacityUnit ?? undefined,
    wasteStreamId: values.wasteStreamId ?? undefined,
    estimatedPickupDate: toDateTimeInput(values.estimatedPickupDate),
    ...(mode === "create"
      ? {
          initialAmount: safeParseFloat(values.initialAmount) ?? undefined,
          initialAmountUnit: values.initialAmountUnit ?? undefined,
          accumulationStartDate: toDateTimeInput(values.accumulationStartDate),
          movedToCentralDate: toDateTimeInput(values.movedToCentralDate),
        }
      : { accumulationAreaId: currentAccumulationAreaId ?? undefined }),
  };
}

/**
 * Update mutation input. Shares the common scalars with create via
 * toSharedContainerFields; the nullable fields diverge deliberately — here a
 * null is an instruction to clear (detach the stream, drop the capacity/unit),
 * whereas create coalesces the same fields to undefined ("leave unset").
 */
function toContainerUpdateInput(
  values: WasteContainerFormValues,
  containerId: string,
  estimatedPickupDateDirty: boolean
): WasteContainerUpdateInput {
  return {
    ...toSharedContainerFields(values),
    // A cleared label arrives as "" so the mutation's blank guard rejects it
    // instead of Prisma leaving the stored label unchanged.
    containerLabel: values.containerLabel.trim(),
    id: containerId,
    capacity: safeParseFloat(values.capacity),
    capacityUnit: values.capacityUnit,
    wasteStreamId: values.wasteStreamId,
    // Only send the date when the user actually touched it: re-normalizing the
    // stored instant through the browser's local zone can shift it to a
    // different calendar day for negative-UTC-offset users.
    estimatedPickupDate: estimatedPickupDateDirty
      ? (toDateTimeInput(values.estimatedPickupDate) ?? null)
      : undefined,
  };
}

function missingFacilityIssue(): Issue {
  return {
    level: IssueLevel.Critical,
    key: "facilityId",
    message: "Select a facility for this container",
    modelName: "WasteContainer",
    dataName: null,
    jurisdictions: [],
  };
}

function useWasteContainerInputValidation(
  mode: "create" | "edit",
  currentAccumulationAreaId?: string | null,
  currentContainerId?: string | null
) {
  const [query] = useLazyQuery(VALIDATE_WASTE_CONTAINER_INPUT, {
    fetchPolicy: "no-cache",
  });

  return useCallback(
    async (values: WasteContainerFormValues): Promise<Issue[]> => {
      if (!values.facilityId) {
        return [missingFacilityIssue()];
      }
      const { data } = await query({
        variables: {
          input: toContainerInput(values, mode, currentAccumulationAreaId),
          // Editing: the container's own label must not read as a collision.
          currentContainerId: currentContainerId ?? undefined,
        },
      });
      return data?.wasteContainerInputValidator ?? [];
    },
    [query, mode, currentAccumulationAreaId, currentContainerId]
  );
}

export type WasteContainerDialogProps = {
  onClose: () => void;
  /** Locks the create form to this facility (facility-scoped list, tile). */
  facilityId?: string;
  /** Present in edit mode. */
  container?: WasteContainerDetail;
  onCreated?: (containerId: string) => void;
};

export function WasteContainerDialog({
  onClose,
  facilityId,
  container,
  onCreated,
}: WasteContainerDialogProps) {
  const alerts = useAlerts();
  const mode: "create" | "edit" = container ? "edit" : "create";

  const [selectedWasteStream, setSelectedWasteStream] = useState<WasteStreamOption | null>(
    container?.wasteStream ?? null
  );

  const defaultValues = useMemo<WasteContainerFormValues>(
    () => ({
      facilityId: container?.facilityId ?? facilityId ?? "",
      containerLabel: container?.containerLabel ?? "",
      containerType: container?.containerType ?? "",
      capacity: container?.capacity != null ? String(container.capacity) : "",
      capacityUnit: container?.capacityUnit ?? null,
      isPermanent: container?.isPermanent ?? false,
      isFlowThrough: container?.isFlowThrough ?? false,
      wasteStreamId: container?.wasteStream?.id ?? null,
      initialAmount: "",
      initialAmountUnit: null,
      isEstimate: container?.isEstimate ?? false,
      accumulationStartDate: null,
      movedToCentralDate: null,
      sqgOver200Miles: container?.sqgOver200Miles ?? false,
      estimatedPickupDate: container?.estimatedPickupDate ?? null,
    }),
    [container, facilityId]
  );

  const validateInput = useWasteContainerInputValidation(
    mode,
    container?.accumulationArea?.id ?? null,
    container?.id ?? null
  );
  const form = useValidatingForm<WasteContainerFormValues>(defaultValues, [], validateInput);
  const {
    control,
    getValues,
    setValue,
    watch,
    issues,
    formState: { isDirty, dirtyFields },
  } = form;

  const watchedFacilityId = watch("facilityId");
  const watchedIsPermanent = watch("isPermanent");

  // Placement is create-only, and a tank must sit in a central area. A
  // container already in a satellite area can never be made permanent from
  // this form, so lock the checkbox instead of letting the user walk into an
  // unresolvable critical placement issue that leaves Save disabled.
  const tankPlacementLocked =
    mode === "edit" &&
    container?.accumulationArea != null &&
    container.accumulationArea.type !== WasteAccumulationAreaType.Central;

  // Selecting a container type prefills capacity from the industry-standard
  // size when the user hasn't entered one.
  const prefillCapacityForType = useCallback(
    (typeCode: string) => {
      if (!isWasteContainerTypeCode(typeCode)) {
        return;
      }
      const definition = WASTE_CONTAINER_TYPE_BY_CODE[typeCode];
      if (definition.defaultCapacity == null || definition.defaultCapacityUnit == null) {
        return;
      }
      if (getValues("capacity").trim() !== "") {
        return;
      }
      setValue("capacity", String(definition.defaultCapacity), { shouldDirty: true });
      setValue("capacityUnit", toGraphqlUnitType(definition.defaultCapacityUnit), {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [getValues, setValue]
  );

  const [createContainer, { loading: isCreating }] = useMutation(CREATE_WASTE_CONTAINER_MUTATION, {
    refetchQueries: [
      "TenantWasteContainers",
      "FacilityWasteContainers",
      "FacilityWasteContainersCount",
    ],
  });
  const [updateContainer, { loading: isUpdating }] = useMutation(UPDATE_WASTE_CONTAINER_MUTATION, {
    refetchQueries: ["TenantWasteContainers", "FacilityWasteContainers", "GetWasteContainer"],
  });

  const onSubmit = async () => {
    const values = getValues();
    // Warn-and-allow gate: CRITICAL issues block the save; ERROR issues have
    // already been surfaced on the fields and in the issue list but the user
    // may still save through them.
    const submitIssues = await validateInput(values);
    if (hasCriticalIssues(submitIssues)) {
      const firstCritical = submitIssues.find((issue) => issue.level === IssueLevel.Critical);
      alerts.error(firstCritical?.message ?? "Fix the critical issues before saving");
      return;
    }
    if (mode === "create") {
      await createContainer({
        variables: { input: toContainerInput(values, "create") },
        onCompleted: (data) => {
          alerts.success("Container created");
          onCreated?.(data.createWasteContainer.id);
          onClose();
        },
        onError: (error) => {
          alerts.error(error.message ?? "An error occurred while creating the container");
        },
      });
      return;
    }
    if (!container) {
      return;
    }
    const input = toContainerUpdateInput(
      values,
      container.id,
      Boolean(dirtyFields.estimatedPickupDate)
    );
    await updateContainer({
      variables: { input },
      onCompleted: () => {
        alerts.success("Container updated");
        onClose();
      },
      onError: (error) => {
        alerts.error(error.message ?? "An error occurred while updating the container");
      },
    });
  };

  const isSaving = isCreating || isUpdating;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      title={mode === "create" ? "Add container" : "Edit container"}
      primaryActionButtonProps={{
        loading: isSaving,
        onClick: onSubmit,
        disabled: hasCriticalIssues(issues) || !isDirty,
      }}
      additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
    >
      <FormProvider {...form}>
        <Box component="form" sx={{ pt: 1 }}>
          <Grid container spacing={2}>
            {mode === "create" && !facilityId && (
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="facilityId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FacilityPicker
                      label="Facility"
                      facilityQueryVariant="waste"
                      textFieldProps={{ required: true }}
                      value={field.value || null}
                      onChange={(facility) => {
                        const nextFacilityId = facility?.id ?? "";
                        // A waste stream belongs to a facility; carrying the
                        // old selection across a facility switch dead-ends
                        // the save on "belongs to a different facility".
                        if (nextFacilityId !== field.value) {
                          setValue("wasteStreamId", null, { shouldValidate: true });
                          setSelectedWasteStream(null);
                        }
                        field.onChange(nextFacilityId);
                      }}
                      {...fieldState}
                    />
                  )}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <FormTextField
                name="containerLabel"
                label="Label"
                control={control}
                helperText={mode === "create" ? "Leave blank to auto-generate a label" : undefined}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                name="containerType"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    select
                    fullWidth
                    required
                    label="Container type"
                    value={field.value}
                    onChange={(event) => {
                      field.onChange(event.target.value);
                      prefillCapacityForType(event.target.value);
                    }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  >
                    {CONTAINER_TYPE_SELECT_ITEMS.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.display}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 7 }}>
              <FormTextField name="capacity" label="Capacity" control={control} type="number" />
            </Grid>
            <Grid size={{ xs: 5 }}>
              <FormSelect
                name="capacityUnit"
                label="Capacity unit"
                control={control}
                selectItems={UNIT_SELECT_ITEMS}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <AsyncAutocomplete
                query={FACILITY_WASTE_STREAM_OPTIONS_QUERY}
                variables={{ facilityId: watchedFacilityId }}
                getItems={(data: FacilityWasteStreamOptionsQuery) =>
                  data.facility?.wasteStreams?.items ?? []
                }
                getOptionLabel={(stream: WasteStreamOption) => stream.name}
                value={selectedWasteStream}
                onChange={(stream) => {
                  setSelectedWasteStream(stream);
                  setValue("wasteStreamId", stream?.id ?? null, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
                disabled={!watchedFacilityId}
                lazyLoad
                fullWidth
                textFieldProps={{ label: "Waste stream" }}
                initialSortModel={[{ field: "name", sort: "asc" }]}
              />
            </Grid>
            {mode === "create" && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Accumulation area"
                  fullWidth
                  disabled
                  helperText="Accumulation areas arrive with placement"
                />
              </Grid>
            )}
            {mode === "create" && (
              <>
                <Grid size={{ xs: 7 }}>
                  <FormTextField
                    name="initialAmount"
                    label="Initial amount"
                    control={control}
                    type="number"
                    helperText="Recorded as the container's first fill event"
                  />
                </Grid>
                <Grid size={{ xs: 5 }}>
                  <FormSelect
                    name="initialAmountUnit"
                    label="Amount unit"
                    control={control}
                    selectItems={UNIT_SELECT_ITEMS}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <DateField
                    name="accumulationStartDate"
                    label="Accumulation start date"
                    control={control}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <DateField
                    name="movedToCentralDate"
                    label="Moved to central date"
                    control={control}
                  />
                </Grid>
              </>
            )}
            <Grid size={{ xs: mode === "create" ? 6 : 12 }}>
              <DateField
                name="estimatedPickupDate"
                label="Estimated pickup date"
                control={control}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <CheckboxField
                name="isEstimate"
                label="Amounts are estimates"
                control={control}
                noErrorDisplay
              />
              <CheckboxField
                name="isPermanent"
                label="Permanent vessel (tank)"
                control={control}
                disabled={tankPlacementLocked}
                tooltip={
                  tankPlacementLocked
                    ? "Tanks can only be placed in central accumulation areas. This container is in a satellite area, so it can't be made permanent here."
                    : undefined
                }
                checkboxProps={{
                  onChange: (_event, checked) => {
                    // Flow-through requires permanent; clear it when permanent
                    // is unchecked, or the disabled flow-through checkbox holds
                    // a stale true the user can no longer clear.
                    if (!checked) {
                      setValue("isFlowThrough", false, { shouldDirty: true, shouldValidate: true });
                    }
                  },
                }}
                noErrorDisplay
              />
              <CheckboxField
                name="isFlowThrough"
                label="Flow-through (clock resets when emptied)"
                control={control}
                disabled={!watchedIsPermanent}
                noErrorDisplay
              />
              <CheckboxField
                name="sqgOver200Miles"
                label="SQG shipping 200+ miles to the designated facility"
                control={control}
                noErrorDisplay
              />
            </Grid>
            {mode === "edit" && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="info" variant="outlined">
                  <Typography variant="body2">
                    Placement, amounts, and status change through container moves and fill events,
                    not from this form.
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </Box>
      </FormProvider>
    </Dialog>
  );
}
